import {
  ProductRecommendations,
  ProductRecommendationsList,
  ProductRecommendationItem,
  ProductRecommendationsResponse
} from '../../types/Recommendations'
import { ReviewsResponse } from '../../types/Contentful/ContentfulTypes'
import { MIN_RECOMMENDATIONS_REQUIRED } from '../../constants/recommendationsEligibility'
import { CalloutData } from '@vp/ep-tiles'
import { createMcsAdapter, type AdaptedResponse } from '@vp/product-recommendations-tracking'

export const MOBILE_TILES_PER_ROW = 2
export const MOBILE_COL_SPAN = 6

export const getDesktopTilesPerRow = (tileCount: number, sidebarEnabled: boolean): 4 | 6 => {
  if (tileCount <= 4 || sidebarEnabled) return 4
  return 6
}

export const getEffectiveTilesPerRow = (
  desktopTilesPerRow: 4 | 6,
  screenClass: string
): 2 | 4 | 6 => (screenClass === 'xs' ? MOBILE_TILES_PER_ROW : desktopTilesPerRow)

export const adaptMcsResponse = (
  raw: ProductRecommendations | undefined
): AdaptedResponse | null => {
  if (!raw) return null
  return createMcsAdapter().adaptResponse(raw)
}

export const sortProductsByStock = (
  products: ProductRecommendationItem[]
): ProductRecommendationItem[] =>
  [...products].sort((a, b) => Number(a.isOutOfStock) - Number(b.isOutOfStock))

export const hasEnoughProducts = (recommendations: ProductRecommendations | undefined): boolean =>
  (recommendations?.products?.length ?? 0) >= MIN_RECOMMENDATIONS_REQUIRED

export const getRecommendationMpvIds = (
  rawProductRecommendations: ProductRecommendationsResponse | null | undefined,
  bestsellersEligible: boolean,
  newArrivalsEligible: boolean
): string[] =>
  [
    ...(bestsellersEligible ? (rawProductRecommendations?.bestsellers?.products ?? []) : []),
    ...(newArrivalsEligible ? (rawProductRecommendations?.newArrivals?.products ?? []) : [])
  ].map((p) => p.productId)

export const buildRecommendationsModel = (
  recommendations: ProductRecommendations,
  reviewsData: ReviewsResponse,
  outOfStockByMpvId: Record<string, boolean>,
  calloutsData: Record<string, CalloutData> = {}
): ProductRecommendationsList => {
  const mappedProducts: ProductRecommendationItem[] = recommendations.products.map((product) => {
    const review = reviewsData[product.productId]
    return {
      mpvId: product.productId,
      productKey: product.coreProductId,
      name: product.title,
      description: product.description,
      image: {
        src: product.imageUrl,
        alt: product.title
      },
      productUrl: product.productUrl,
      productVersion: product.coreProductVersion,
      selectedOptions: product.selectedAttributes,
      isOutOfStock: outOfStockByMpvId[product.productId] ?? false,
      // product.productId is the mpvId in the raw MCS response — calloutsData is keyed the same way
      callout: calloutsData[product.productId],
      ratingSummary: review
        ? { reviewsCount: review.reviewsCount, averageRating: review.averageRating }
        : null
    }
  })

  return { products: mappedProducts }
}
